import { t } from 'i18next';
import { memo } from 'react';
import { ChevronRight, ExitDeleteRemoveClose } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { InlineMessageSeverity } from '../../stores/types/inlineMessageBanner.interface';
import { Button, ButtonSize, ButtonType } from './Button.component';
import { Icon } from './Icon.component';
import { Label, LabelType } from './Label.component';
import { ColorType } from './shared/Colors.g';

interface InlineMessageVariantStyle {
  container: string;
  defaultIcon: string;
}

const VARIANT_STYLES: Record<InlineMessageSeverity, InlineMessageVariantStyle> = {
  [InlineMessageSeverity.INFORMATION]: {
    container: 'border-spruce bg-denim_light',
    // Use empty string to skip SvgColor recolor; use with SVGs that define this gradient.
    defaultIcon: '',
  },
  [InlineMessageSeverity.WARNING]: {
    container: 'border-warning bg-warning_light',
    defaultIcon: ColorType.WARNING,
  },
  [InlineMessageSeverity.ALERT]: {
    container: 'border-negative bg-negative_light',
    defaultIcon: ColorType.NEGATIVE,
  },
};

export interface InlineMessageProps {
  /** Visual tone; drives border, background, and default icon tint. */
  severity: InlineMessageSeverity;
  /** Primary headline; stacks above `supportingMessage` when `onBtnClick` is present, otherwise flows inline with it. */
  leadMessage: string;
  /** Secondary line shown next to or below the headline. */
  supportingMessage?: string;
  /** Optional SVG import passed to `Icon`; when omitted, no leading icon is shown. */
  leadingIcon?: string;
  /** Optional outline button with chevron (e.g. session-banner CTA). */
  onBtnClick?: () => void;
  /** When provided, shows the top-right dismiss control. */
  onDismiss?: () => void;
  /** `data-testid` on the outer banner for automated tests; nested controls use this string plus `-action`, `-action-button`, or `-dismiss`. */
  dataTestId: string;
}

export const InlineMessage = memo(
  ({
    severity,
    leadMessage,
    supportingMessage,
    leadingIcon,
    onBtnClick,
    onDismiss,
    dataTestId,
  }: InlineMessageProps) => {
    const styles = VARIANT_STYLES[severity];

    return (
      <div
        className={`relative flex h-auto w-full max-w-full min-w-0 items-center justify-between rounded border py-3 pl-3 ${
          onDismiss ? 'pr-11' : 'pr-3'
        } ${styles.container}`}
        data-testid={dataTestId}
      >
        {leadingIcon && (
          <div className='shrink-0 pr-3' aria-hidden>
            <Icon src={leadingIcon} size={IconSize.SIZE_24x24} color={styles.defaultIcon} />
          </div>
        )}

        <div className='min-w-0 flex-1'>
          {onBtnClick ? (
            <div className='space-y-1'>
              <Label text={leadMessage} type={LabelType.LABEL_S} className='m-0' />
              {supportingMessage != null && (
                <Label text={supportingMessage} type={LabelType.BODY3} className='m-0' />
              )}
            </div>
          ) : (
            <div>
              <Label text={leadMessage} type={LabelType.LABEL_S} className='m-0 inline' />
              {supportingMessage != null && (
                <>
                  {' '}
                  <Label text={supportingMessage} type={LabelType.BODY3} className='m-0 inline' />
                </>
              )}
            </div>
          )}
        </div>

        {onBtnClick && (
          <div className='shrink-0 self-center' data-testid={`${dataTestId}-action`}>
            <Button
              type={ButtonType.OUTLINE}
              size={ButtonSize.MINI}
              label={t('learn_more')}
              onClick={onBtnClick}
              icon={ChevronRight}
              iconRightAlign
              iconSize={IconSize.SIZE_16x16}
              iconColor={ColorType.SPRUCE}
              labelType={LabelType.LABEL_S_MEDIUM}
              labelColor={ColorType.SPRUCE}
              dataTestId={`${dataTestId}-action-button`}
            />
          </div>
        )}

        {onDismiss && (
          <div className='absolute right-3 top-3 z-10'>
            <Icon
              src={ExitDeleteRemoveClose}
              size={IconSize.SIZE_16x16}
              color={ColorType.GREY4}
              onClick={onDismiss}
              dataTestId={`${dataTestId}-dismiss`}
            />
          </div>
        )}
      </div>
    );
  },
);

InlineMessage.displayName = 'InlineMessage';
